// Cliente HTTP do cardápio digital. Consome o backend REAL:
//   GET  {VITE_API_BASE}/api/v1/public/cardapio/{slug}          → CardapioResponse
//   POST {VITE_API_BASE}/api/v1/public/cardapio/{slug}/pedidos  → PedidoConfirmado
//
// ─── FONTE ÚNICA DO CONTRATO ─────────────────────────────────────────────────
// TODO acoplamento ao backend mora AQUI (tipos + fetch + normalize + placePedido).
// Se um NOME de campo mudar no backend, o conserto é de UM arquivo só: a UI fala
// exclusivamente com o view-model `StoreProduto`/`Cardapio`, nunca com o JSON cru.
//
// Contrato ESTENDIDO (campos novos são ADITIVOS — se o backend ainda não os enviou,
// caem para vazio e a tela renderiza mesmo assim):
//   { tenantNome, whatsapp?, categorias: [ { nome, produtos: [ {
//       produtoId, nome, descricao, preco, sku,
//       variacoes?: [ { id, nome, preco } ],
//       adicionalGrupos?: [ { id, nome, min, max, obrigatorio, opcoes: [ { id, nome, preco } ] } ]
//   } ] } ] }
// `preco` é BigDecimal no backend, serializado como number (Jackson).

import { apiBase } from './config';
import { safeExternalUrl } from './url';
import { newIdempotencyKey } from './order';
import { clampQty } from './bag';

// ─── View-model (o que a UI consome) ────────────────────────────────────────
export interface StoreVariacao {
  id: string;
  nome: string;
  preco: number;
  // Fase 7.2 — porção/rendimento por tamanho (null = não informado). Exibidos no detalhe da variação.
  porcaoDescricao: string | null;
  rendimentoPessoas: number | null;
}

export interface StoreAdicionalOpcao {
  id: string;
  nome: string;
  preco: number;
  /**
   * Grupo de onde a opção veio, e a política dele (V315). Necessários porque num grupo `MAIOR`
   * ("meia a meia") só a opção mais cara entra na conta: sem isto a sacola somaria, e o cliente veria
   * um valor e o servidor cobraria outro. Ausentes = SOMA, o comportamento de sempre.
   */
  grupoId?: string;
  politicaGrupo?: 'SOMA' | 'MAIOR';
}

export interface StoreAdicionalGrupo {
  id: string;
  nome: string;
  /** mínimo de opções a escolher no grupo (0 = livre). */
  min: number;
  /** máximo de opções (0 = sem limite). */
  max: number;
  obrigatorio: boolean;
  /** Ausente = SOMA (todo grupo já cadastrado). `MAIOR` cobra só a opção mais cara escolhida. */
  politicaPreco?: 'SOMA' | 'MAIOR';
  opcoes: StoreAdicionalOpcao[];
}

export interface StorePizza {
  politicaPreco: 'MAIOR_SABOR' | 'MEDIA_PONDERADA' | 'BASE_MAIS_ACRESCIMOS';
  permiteMisturaDoceSalgada: boolean;
  tamanhos: { id: string; nome: string; fatias: number; maxSabores: number; precoBase: number; ordem: number }[];
  sabores: { id: string; nome: string; descricao: string | null; tipo: 'SALGADO' | 'DOCE'; precos: { tamanhoId: string; preco: number }[] }[];
  bordas: { id: string; nome: string; preco: number; ordem: number }[];
  /** Adicionais DENTRO da pizza (V388): só os ativos, pela ordem. Ausente = fixture/servidor anterior (nenhum). */
  adicionais?: { id: string; nome: string; preco: number; ordem: number }[];
}

export interface MontagemPizzaPayload {
  tamanhoId: string;
  sabores: { saborId: string; partes: number }[];
  bordaId?: string | null;
  /** Adicionais DENTRO da pizza (V388): cada um uma vez, até 7. Ausente = nenhum. */
  adicionalIds?: string[];
}

/**
 * Tabela nutricional por porção (Fase 7.1, ANVISA RDC 429/2020). Todos os campos são opcionais (`null` =
 * não informado); a UI só mostra a linha do que veio preenchido. `null` como um todo = produto sem tabela.
 */
export interface Nutricional {
  porcaoG: number | null;
  medidaCaseira: string | null;
  valorEnergeticoKcal: number | null;
  carboidratosG: number | null;
  acucaresTotaisG: number | null;
  acucaresAdicionadosG: number | null;
  proteinasG: number | null;
  gordurasTotaisG: number | null;
  gordurasSaturadasG: number | null;
  gordurasTransG: number | null;
  fibraAlimentarG: number | null;
  sodioMg: number | null;
}

/** Produto no view-model da loja. `produtoId` É o id — a UI nunca vê o JSON cru. */
export interface StoreProduto {
  produtoId: string;
  nome: string;
  /** O RESUMO: a linha curta do produto. */
  descricao: string | null;
  /** Pendência 15 — a DESCRIÇÃO LONGA, que o detalhe mostra abaixo do resumo. Backend anterior/sem ela → null. */
  descricaoLonga?: string | null;
  preco: number;
  sku: string | null;
  categoria: string;
  /** Versão da imagem (V200): cache-buster do ?v na URL da foto. Default 0 se ausente. */
  imagemVersao: number;
  /**
   * Tem foto (QA L5): sinal EXPLÍCITO de imagem presente (backend deriva de imagem_content_type != null). A
   * loja só pede a <img> quando true — não usa imagemVersao>0 (dava falso-negativo em foto pré-V200 com
   * versão 0). Default false (backend antigo sem o campo → sem foto, sem 404 desnecessário).
   */
  temImagem: boolean;
  variacoes: StoreVariacao[];
  adicionalGrupos: StoreAdicionalGrupo[];
  /**
   * ESGOTADO (estoque): true quando a categoria controla estoque e o saldo é <= 0. A loja marca "Esgotado"
   * e desabilita a escolha. Feito sob demanda (categoria sem controle) nunca esgota. Default false se ausente.
   */
  esgotado: boolean;
  /**
   * Fase 7.1 — alérgenos EFETIVOS (produto ∪ ficha) como CÓDIGOS do enum (GLUTEN, LEITE, …); a UI traduz para
   * pt-BR via `rotulosAlergenos`. `contem` = declara que contém; `podeConter` = traços/contaminação cruzada.
   * Dietas declaradas (só viram selo quando true). Todos default vazio/false (backend antigo → nada exibido).
   */
  alergenosContem: string[];
  alergenosPodeConter: string[];
  vegetariano: boolean;
  vegano: boolean;
  nutricional: Nutricional | null;
  // Fase 7.2 — ids das fotos ADICIONAIS (galeria), ordenados. A loja monta o carrossel com a URL
  // /api/v1/public/{slug}/produtos/{produtoId}/galeria/{id}. Vazio = só a foto principal (ou emoji).
  galeriaImagemIds: string[];
  pizza?: StorePizza | null;
}

/** Alias de compatibilidade com o código de leitura anterior. */
export type ProdutoCardapio = StoreProduto;

export interface CategoriaCardapio {
  nome: string;
  produtos: StoreProduto[];
}

/**
 * Zona de entrega ATIVA (Fase 1.1): liga um BAIRRO a uma taxa, pedido mínimo e prazo estimado. O storefront
 * acha a zona do bairro do cliente e EXIBE a taxa antes de pagar. O backend recalcula na submissão (o cliente
 * nunca envia frete). Lista vazia = a loja não usa zonas (entrega "a combinar", comportamento legado).
 */
export interface ZonaEntrega {
  bairro: string;
  cidade: string | null;
  taxa: number;
  pedidoMinimo: number;
  prazoEstimadoMin: number | null;
  // Fase 3.2 — frete grátis acima deste subtotal; null = sem frete grátis.
  freteGratisAcima: number | null;
}

/**
 * Rua com taxa de entrega própria (V443; dono 21/10/2026 — Don Corderone: grátis até 500 m da pizzaria). `chave` é a
 * do servidor (ChaveLogradouro): o checkout normaliza a rua digitada do mesmo jeito (chaveLogradouro.ts) e compara.
 * Vale ANTES da zona do bairro e da taxa padrão.
 */
export interface RuaEntrega {
  logradouro: string;
  chave: string;
  cidade: string | null;
  taxa: number;
}

export interface Cardapio {
  tenantNome: string;
  /** Link/telefone de WhatsApp do estabelecimento (opcional). */
  whatsapp: string | null;
  /** IDs de produtos em destaque ("Mais vendidos"), na ordem de exibição (pode ser vazio). */
  destaques: string[];
  /** IDs de produtos em oferta (trilho "Ofertas"), lista curada na ordem de exibição (pode ser vazio). */
  ofertas: string[];
  categorias: CategoriaCardapio[];
  /**
   * CAPA/banner da loja (V205): versão (cache-buster) quando há capa; null = sem capa. O StoreView monta a
   * URL `${apiBase()}/api/v1/public/${slug}/capa?v=${capaVersao}` e a usa como background do herói.
   */
  capaVersao: number | null;
  /** Zonas de entrega ATIVAS (Fase 1.1). Vazio = a loja não usa zonas (vale a taxa padrão, se houver). */
  zonasEntrega: ZonaEntrega[];
  /** Ruas com taxa própria (V443, 21/10/2026). Ausente/vazio = nenhuma. */
  ruasEntrega?: RuaEntrega[];
  /** Taxa de entrega PADRÃO da loja quando ligada (21/10/2026); null = desligada (frete "a combinar"). */
  taxaEntregaPadrao?: number | null;
  /** Fuso da LOJA (QA 31/10/2026): dia e hora do agendamento saem nele, nunca no do celular. Ausente = Brasília. */
  fusoHorario?: string;
}

// ─── Pedido (POST) ───────────────────────────────────────────────────────────
export type TipoEntrega = 'ENTREGA' | 'RETIRADA';

/** Endereço de entrega — forma do FORMULÁRIO (UI). No corpo do POST vai ACHATADO numa string
 *  única (o backend guarda `endereco` como texto, VARCHAR(500), igual ao `notes` do Vendeon). */
export interface PedidoEndereco {
  cep?: string;
  logradouro?: string;
  // NÚMERO em campo PRÓPRIO (pedido do dono 10/09): o backend usa CEP+número como IDENTIDADE do local para
  // reconhecer o MESMO endereço do 2º pedido em diante, em vez de duplicar o cadastro do cliente — número
  // embutido no meio do logradouro não serve para essa comparação.
  numero?: string;
  bairro?: string;
  cidade?: string;
  // UF vem da própria consulta de CEP; completa o endereço gravado no cadastro do cliente.
  uf?: string;
  complemento?: string;
}

export interface PedidoItemPayload {
  produtoId: string;
  quantidade: number;
  variacaoId?: string;
  adicionalIds: string[];
  observacao?: string;
  montagemPizza?: MontagemPizzaPayload;
}

export interface PedidoPayload {
  idempotencyKey: string;
  clienteNome: string;
  clienteContato: string;
  tipoEntrega: TipoEntrega;
  endereco?: string; // texto achatado (logradouro, bairro, cidade, complemento, CEP)
  // BAIRRO estruturado (Fase 1.1): o backend calcula a taxa de entrega pela ZONA do bairro. cidade fica
  // reservada para futuro (multi-cidade); no fluxo atual o casamento é por bairro (cidade omitida/null).
  bairro?: string;
  cidade?: string | null;
  // Endereço ESTRUTURADO (10/09): cep/numero/uf em campos próprios (@Size 9/20/2 em
  // CriarPedidoCardapioRequest). Com eles o backend grava o endereço no CADASTRO do cliente e, do 2º pedido
  // em diante, reconhece o MESMO local (identidade = CEP+número). Ausentes, o pedido funciona como antes.
  cep?: string;
  // LOGRADOURO isolado. `endereco` (acima) é o texto ACHATADO do cupom/rota, que JÁ inclui número, bairro,
  // cidade e CEP — mandar aquele texto como logradouro deixaria a rua do cadastro como
  // "Rua X, 123, Centro, Bauru (CEP …)", com o número duplicado dentro dela.
  logradouro?: string;
  numero?: string;
  uf?: string;
  formaPagamento: string;
  observacoes?: string;
  itens: PedidoItemPayload[];
  // AGENDAMENTO (Fase 2.1): instante de início da janela escolhida (ISO). Ausente = "o quanto antes".
  agendadoPara?: string;
  // Cliente EMPRESA (PJ): natureza + CNPJ opcional (pode ser alfanumérico). Ausente ⇒ pessoa física.
  personType?: string;
  documento?: string;
  // PREÇO/FRETE NUNCA SÃO ENVIADOS — o backend reprecifica os itens e recalcula a taxa pela zona do bairro.
}

/** Slot de agendamento ofertado pela loja (Fase 2.1). `disponivel=false` = lotado (a UI desabilita). */
export interface Slot {
  inicio: string; // ISO instant
  fim: string;
  capacidade: number;
  restantes: number;
  disponivel: boolean;
}

export interface PedidoConfirmado {
  numero: number;
  status: string;
  /**
   * Total dos ITENS (server-priced). O frete vem à parte em {@link taxaEntrega}. QA lote4 #16 — `number | null`:
   * `null` distingue "backend NÃO devolveu o campo" (contrato regrediu → o front cai no subtotal do cliente)
   * de "devolveu 0 legítimo" (pedido 100% cortesia). Antes vinha coagido a 0 (toNum) e o guard `!= null` no
   * Checkout era código morto — um 2xx sem `total` exibia R$ 0,00 em vez do subtotal.
   */
  total: number | null;
  /** Taxa de entrega aceita (server, pela zona); `null` se o backend não devolveu (front cai no preview). */
  taxaEntrega: number | null;
}

// Confirmação do pedido de MESA. Diferente do cardápio online (LEAD com `numero` sequencial), o pedido
// de mesa cria um Pedido REAL — que não tem número sequencial —, então o backend responde
// { pedidoId, status, valorTotal }. Modelamos exatamente esse contrato (sem inventar `numero`/`total`,
// que vinham undefined e travavam a tela de sucesso + induziam reenvio/duplicidade).
export interface PedidoMesaConfirmado {
  pedidoId: string;
  status: string;
  total: number;
}

// ─── Experiência na mesa (QR) ───────────────────────────────────────────────
export interface MesaEntrada {
  sessaoId: string;
  participanteId: string;
  participanteNome: string;
  token: string;
  mesaNumero: number;
  estabelecimento: string;
  tenantSlug: string;
  /**
   * F3-M40 — TAXA DE CONSUMO (%) que a loja VAI cobrar sobre os itens desta mesa
   * (EntradaMesaPublicaResponse.taxaConsumoPercentual = o MESMO percentual que o backend snapshota no
   * pedido). Sem ela, a revisão do pedido mostrava "Total estimado R$ 100,00" numa loja com 10% ligado e a
   * comanda fechava em R$ 110,00 — 10% de surpresa no caixa. Opcional: backend anterior ao campo (ou
   * ausência no JSON) = SEM taxa, exatamente a tela de antes.
   */
  taxaConsumoPercentual?: number;
  expiraEm: string;
}

// ─── Erro tipado ─────────────────────────────────────────────────────────────
// 'unavailable' → gate amigável (403/404/401/slug inexistente). NUNCA stack trace.
// 'network'     → falha de rede/timeout/5xx → mensagem + "tentar de novo".
export type CardapioErrorKind = 'unavailable' | 'network';

export class CardapioError extends Error {
  readonly kind: CardapioErrorKind;
  readonly status?: number;
  // `code` estável do ProblemDetail do backend (ex.: SLOT_INDISPONIVEL, PRODUTO_INDISPONIVEL) — permite ao
  // storefront tratar o erro inline (reconduzir/revalidar) em vez de só exibir a mensagem. QA bloco13 #4.
  readonly code?: string;
  /** QA-28 M-14 — erros de VALIDAÇÃO por campo do backend (ProblemDetail.errors[], ex.:
   *  "endereco: tamanho deve ser entre 0 e 500"). O `detail` desses 422 é fixo ("Erro de validação nos dados
   *  enviados") e não diz QUAL campo — sem carregar `errors` o cliente lia uma frase inútil e, com a
   *  idempotencyKey estável, repetia o mesmo erro para sempre. */
  readonly errors?: string[];
  constructor(
    kind: CardapioErrorKind,
    message: string,
    status?: number,
    code?: string,
    errors?: string[]
  ) {
    super(message);
    this.name = 'CardapioError';
    this.kind = kind;
    this.status = status;
    this.code = code;
    this.errors = errors;
  }
}

async function mesaRequest<T>(
  codigo: string,
  caminho: string,
  init: RequestInit = {},
  token?: string
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body) headers.set('Content-Type', 'application/json');
  if (token) headers.set('X-Mesa-Session-Token', token);
  let response: Response;
  try {
    response = await fetch(
      // QR UNIVERSAL (sem PIN — dono 20/08): o código é POR LOJA e os endpoints ficam sob /mesa/t/{codigo}.
      `${apiBase()}/api/v1/public/mesa/t/${encodeURIComponent(codigo)}${caminho}`,
      { ...init, headers }
    );
  } catch {
    throw new CardapioError('network', 'Não foi possível conectar. Confira sua internet.');
  }
  if (!response.ok) {
    let message = 'Não foi possível concluir. Tente novamente.';
    let code: string | undefined;
    try {
      const body = (await response.json()) as { detail?: string; message?: string; code?: unknown };
      message = body.detail || body.message || message;
      code = typeof body.code === 'string' ? body.code : undefined;
    } catch {
      // resposta sem JSON: mantém mensagem segura
    }
    // 404 só é "sessão/mesa indisponível" quando o servidor diz que é da SESSÃO (TABLE_SESSION_*) ou quando não diz
    // nada (proxy). Um 404 de outro recurso (ex.: item que sumiu do catálogo) é erro do pedido, não da sessão:
    // tratá-lo como sessão expirada expulsava o cliente da mesa em loop (QA 31/10/2026).
    const sessao404 = response.status === 404 && (code == null || code.startsWith('TABLE_SESSION'));
    throw new CardapioError(
      response.status === 401 || response.status === 403 || sessao404 ? 'unavailable' : 'network',
      message,
      response.status,
      code
    );
  }
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export function entrarMesa(
  codigo: string,
  nome: string,
  numeroMesa: number,
  // Token guardado na ÚLTIMA entrada NESTA mesa. Cenário bar (dono 10/09): cada pessoa tem a SUA conta, então
  // uma entrada sem token cria uma comanda NOVA — e reescanear o QR (ou reabrir a página numa aba nova) abriria
  // uma SEGUNDA CONTA para a mesma pessoa. Com o token, o backend devolve a conta que ela já tem sem criar
  // nada. Um token desconhecido/de sessão morta NÃO bloqueia: o backend cai no fluxo de conta nova.
  token?: string
): Promise<MesaEntrada> {
  // Sem PIN (dono 20/08): o cliente identifica só o NÚMERO DA MESA; a comanda abre sozinha no backend.
  return mesaRequest(codigo, '/entrar', {
    method: 'POST',
    body: JSON.stringify({ nome: nome.trim(), numeroMesa, ...(token ? { token } : {}) }),
  });
}

export async function placeMesaPedido(
  codigo: string,
  token: string,
  bag: import('./bag').BagItem[],
  observacoes = '',
  // Chave de idempotência: DEVE vir do chamador (lib/chavePendente) para o retry reusar a MESMA e o
  // backend deduplicar — gerar uma nova a cada chamada duplicaria o pedido de mesa (debita estoque
  // 2×, imprime 2×) no retry. Fallback só para chamadas sem chave (não deveria acontecer no fluxo).
  idempotencyKey: string = newIdempotencyKey()
): Promise<PedidoMesaConfirmado> {
  // NORMALIZA a resposta do backend (PedidoMesaResponse: pedidoId/status/valorTotal). Antes o JSON cru
  // era lido como { numero, total } (do cardápio online) → ambos undefined → a tela "Enviado" nunca
  // aparecia (gate numero!=null falso) e o cliente reenviava, DUPLICANDO o pedido.
  const body = await mesaRequest<Record<string, unknown>>(codigo, '/pedidos', {
    method: 'POST',
    body: JSON.stringify({
      idempotencyKey,
      observacoes: observacoes.trim() || undefined,
      itens: bag.map((item) => ({
        produtoId: item.produto.produtoId,
        // Defesa em profundidade (QA ALTO): mesmo clamp de bagToItens — este mapeamento NÃO passa por
        // bagToItens (constrói o item inline), então precisa da mesma garantia [1, MAX_QTY]/inteiro.
        quantidade: clampQty(item.qty),
        // Observação por item (QA MÉDIO): antes hardcoded undefined — a sacola já suporta o campo, só
        // não estava sendo repassado ao pedido de mesa. Trim + omite se vazia/whitespace.
        observacao: item.observacao?.trim() || undefined,
        variacaoId: item.variacao?.id,
        adicionalIds: item.adicionais.map((a) => a.id),
        ...(item.montagemPizza ? { montagemPizza: item.montagemPizza } : {}),
      })),
    }),
  }, token);
  const b = body ?? {};
  // QA ALTO (achado #4) — a validação de "sucesso falso" de placePedido é sobre `numero<=0`; o pedido de
  // MESA não tem `numero` sequencial (ver comentário do tipo PedidoMesaConfirmado acima), então essa
  // checagem específica não se aplica aqui. mesaRequest já lança CardapioError p/ qualquer !response.ok,
  // que é o caso real de falha deste endpoint — não há um análogo "200 com corpo inválido" documentado.
  return { pedidoId: toStr(b.pedidoId), status: toStr(b.status), total: toNum(b.valorTotal) };
}

// ─── Helpers de coerção defensiva ────────────────────────────────────────────
function toNum(v: unknown): number {
  const n = typeof v === 'number' ? v : Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function toStr(v: unknown): string {
  return typeof v === 'string' ? v : v == null ? '' : String(v);
}

function toStrOrNull(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function toArr(v: unknown): Record<string, unknown>[] {
  return Array.isArray(v) ? v.map((x) => (x ?? {}) as Record<string, unknown>) : [];
}

/** Array de strings não-vazias (para os códigos de alérgeno). Tolera ausência/nulo → []. */
function toStrArr(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.length > 0) : [];
}

/** Número opcional: só devolve valor quando é um número finito de verdade; senão `null` (não informado). */
function toNumOrNull(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Normaliza a tabela nutricional (Fase 7.1). Ausente/nulo → null. Mesmo quando o objeto vem, se NENHUM campo
 * numérico foi informado, devolve null — a UI não mostra uma tabela vazia.
 */
function normNutricional(raw: unknown): Nutricional | null {
  if (raw == null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const n: Nutricional = {
    porcaoG: toNumOrNull(r.porcaoG),
    medidaCaseira: toStrOrNull(r.medidaCaseira),
    valorEnergeticoKcal: toNumOrNull(r.valorEnergeticoKcal),
    carboidratosG: toNumOrNull(r.carboidratosG),
    acucaresTotaisG: toNumOrNull(r.acucaresTotaisG),
    acucaresAdicionadosG: toNumOrNull(r.acucaresAdicionadosG),
    proteinasG: toNumOrNull(r.proteinasG),
    gordurasTotaisG: toNumOrNull(r.gordurasTotaisG),
    gordurasSaturadasG: toNumOrNull(r.gordurasSaturadasG),
    gordurasTransG: toNumOrNull(r.gordurasTransG),
    fibraAlimentarG: toNumOrNull(r.fibraAlimentarG),
    sodioMg: toNumOrNull(r.sodioMg),
  };
  const temNumero = [
    n.porcaoG, n.valorEnergeticoKcal, n.carboidratosG, n.acucaresTotaisG, n.acucaresAdicionadosG,
    n.proteinasG, n.gordurasTotaisG, n.gordurasSaturadasG, n.gordurasTransG, n.fibraAlimentarG, n.sodioMg,
  ].some((x) => x != null);
  return temNumero ? n : null;
}

function normVariacao(raw: Record<string, unknown>): StoreVariacao {
  return {
    id: toStr(raw.id),
    nome: toStr(raw.nome),
    preco: toNum(raw.preco),
    porcaoDescricao: toStrOrNull(raw.porcaoDescricao),
    rendimentoPessoas: toNumOrNull(raw.rendimentoPessoas),
  };
}

function normAdicionalGrupo(raw: Record<string, unknown>): StoreAdicionalGrupo {
  const opcoes = toArr(raw.opcoes).map((o) => ({
    id: toStr(o.id),
    nome: toStr(o.nome),
    preco: toNum(o.preco),
  }));
  const max = toNum(raw.max);
  return {
    id: toStr(raw.id),
    nome: toStr(raw.nome),
    min: toNum(raw.min),
    max,
    // QA L4 — só INFERE obrigatório de min>0 quando a flag está AUSENTE (backend antigo/mock). O backend
    // público SEMPRE envia `obrigatorio` (boolean): sobrescrever um `false` explícito por min>0 forçava um
    // grupo OPCIONAL com mínimo-quando-escolhido a virar obrigatório no cliente, obrigando o cliente a marcar
    // adicionais dispensáveis — contradizendo o PrecoItemService (0 escolhas é aceito num grupo opcional).
    obrigatorio: raw.obrigatorio === true || (raw.obrigatorio == null && toNum(raw.min) > 0),
    // A política do grupo (QA 31/10/2026): "MAIOR" cobra só a opção mais cara, como o PrecoItemService. Sem ela a tela
    // somava as opções e o servidor gravava outro total (o frete grátis e o pedido mínimo divergiam).
    politicaPreco: toStr(raw.politicaPreco) === 'MAIOR' ? 'MAIOR' : 'SOMA',
    opcoes,
  };
}

function normPizza(raw: unknown): StorePizza | null {
  if (raw == null || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  return {
    politicaPreco: (toStr(p.politicaPreco) || 'MAIOR_SABOR') as StorePizza['politicaPreco'],
    permiteMisturaDoceSalgada: p.permiteMisturaDoceSalgada === true,
    tamanhos: toArr(p.tamanhos).map((t) => ({
      id: toStr(t.id), nome: toStr(t.nome), fatias: toNum(t.fatias), maxSabores: toNum(t.maxSabores),
      precoBase: toNum(t.precoBase), ordem: toNum(t.ordem),
    })),
    sabores: toArr(p.sabores).map((s) => ({
      id: toStr(s.id), nome: toStr(s.nome), descricao: toStrOrNull(s.descricao),
      tipo: (toStr(s.tipo) || 'SALGADO') as 'SALGADO' | 'DOCE',
      // F2-B6 — 0,00 é preço VÁLIDO (promoção legítima); `toNum` (default 0) tratava um `preco` malformado
      // (string não-numérica/nulo) exatamente como um 0 legítimo, virando "sabor grátis" em vez de
      // indisponível. `toNumOrNull` + filtro DESCARTA a entrada malformada — sem `tamanhoId` casado, o resto
      // do app (precoSaborNoTamanho/pizzaUtilizavel) já trata como sabor sem preço ali (nunca `?? 0`).
      precos: toArr(s.precos)
        .map((px) => ({ tamanhoId: toStr(px.tamanhoId), preco: toNumOrNull(px.preco) }))
        .filter((px): px is { tamanhoId: string; preco: number } => px.preco != null),
    })),
    bordas: toArr(p.bordas).map((b) => ({
      id: toStr(b.id), nome: toStr(b.nome), preco: toNum(b.preco), ordem: toNum(b.ordem),
    })),
    // Adicionais DENTRO da pizza (V388). Servidor anterior (sem o campo) = lista vazia. Preço malformado DESCARTA o
    // adicional (como nos sabores, F2-B6): nunca vira um adicional "grátis".
    adicionais: toArr(p.adicionais)
      .map((a) => ({ id: toStr(a.id), nome: toStr(a.nome), preco: toNumOrNull(a.preco), ordem: toNum(a.ordem) }))
      .filter((a): a is { id: string; nome: string; preco: number; ordem: number } => a.preco != null),
  };
}

function normProduto(raw: Record<string, unknown>, categoria: string): StoreProduto {
  return {
    produtoId: toStr(raw.produtoId),
    nome: toStr(raw.nome),
    descricao: toStrOrNull(raw.descricao),
    descricaoLonga: toStrOrNull(raw.descricaoLonga),
    preco: toNum(raw.preco),
    sku: toStrOrNull(raw.sku),
    categoria,
    // Versão da imagem (V200): cache-buster do ?v. Ausente/nulo (backend anterior) → 0 (URL sem versão).
    imagemVersao: raw.imagemVersao != null ? toNum(raw.imagemVersao) : 0,
    // Tem foto (QA L5): backend antigo sem o campo → false (não pede a <img>, sem 404 desnecessário).
    temImagem: raw.temImagem === true,
    variacoes: toArr(raw.variacoes).map(normVariacao),
    adicionalGrupos: toArr(raw.adicionalGrupos).map(normAdicionalGrupo),
    // Esgotado (estoque): backend anterior não envia → false (produto segue selecionável, comportamento legado).
    esgotado: raw.esgotado === true,
    // Fase 7.1 — alérgenos/dietas/nutricional. Backend antigo sem os campos → vazio/false/null (nada exibido).
    alergenosContem: toStrArr(raw.alergenosContem),
    alergenosPodeConter: toStrArr(raw.alergenosPodeConter),
    vegetariano: raw.vegetariano === true,
    vegano: raw.vegano === true,
    nutricional: normNutricional(raw.nutricional),
    galeriaImagemIds: toStrArr(raw.galeriaImagemIds),
    pizza: normPizza(raw.pizza),
  };
}

/**
 * Adapta a resposta CRUA do backend → view-model seguro `Cardapio`. Tolera nulos
 * e campos ausentes (variacoes/adicionalGrupos/whatsapp caem para vazio/null).
 * ESTE é o único ponto de tradução do contrato — mantenha-o assim.
 */
export function normalize(raw: unknown): Cardapio {
  const obj = (raw ?? {}) as Record<string, unknown>;
  const tenantNome = toStr(obj.tenantNome);
  const whatsapp = toStrOrNull(obj.whatsapp);
  const destaques = Array.isArray(obj.destaques)
    ? obj.destaques.map((d) => String(d)).filter((d) => d.length > 0)
    : [];
  // Ofertas (lista curada): backend antigo não envia o campo → cai para vazio (trilho some).
  const ofertas = Array.isArray(obj.ofertas)
    ? obj.ofertas.map((o) => String(o)).filter((o) => o.length > 0)
    : [];
  const categorias: CategoriaCardapio[] = toArr(obj.categorias).map((cat) => {
    const nome = toStr(cat.nome);
    return { nome, produtos: toArr(cat.produtos).map((p) => normProduto(p, nome)) };
  });
  // Capa/banner (V205): versão (cache-buster) quando a loja tem capa; ausente/backend antigo → null.
  const capaVersao = obj.capaVersao != null ? toNum(obj.capaVersao) : null;
  // Zonas de entrega (Fase 1.1): backend antigo não envia → vazio (frete "a combinar", legado).
  const zonasEntrega: ZonaEntrega[] = toArr(obj.zonasEntrega).map((z) => ({
    bairro: toStr(z.bairro),
    cidade: toStrOrNull(z.cidade),
    taxa: toNum(z.taxa),
    pedidoMinimo: toNum(z.pedidoMinimo),
    prazoEstimadoMin: z.prazoEstimadoMin != null ? toNum(z.prazoEstimadoMin) : null,
    freteGratisAcima: z.freteGratisAcima != null ? toNum(z.freteGratisAcima) : null,
  }));
  // Ruas com taxa própria e a taxa padrão (21/10/2026): backend antigo não envia → nenhuma rua, sem padrão.
  const ruasEntrega: RuaEntrega[] = toArr(obj.ruasEntrega).map((r) => ({
    logradouro: toStr(r.logradouro),
    chave: toStr(r.chave),
    cidade: toStrOrNull(r.cidade),
    taxa: toNum(r.taxa),
  }));
  const taxaEntregaPadrao = obj.taxaEntregaPadrao != null ? toNum(obj.taxaEntregaPadrao) : null;
  const fusoHorario = typeof obj.fusoHorario === 'string' && obj.fusoHorario.trim() ? obj.fusoHorario.trim() : 'America/Sao_Paulo';
  return {
    tenantNome, whatsapp, destaques, ofertas, categorias, capaVersao, zonasEntrega, ruasEntrega, taxaEntregaPadrao, fusoHorario,
  };
}

// ─── GET cardápio ────────────────────────────────────────────────────────────
export async function fetchCardapio(
  slug: string,
  signal?: AbortSignal,
  canal?: 'MESA',
): Promise<Cardapio> {
  const cleanSlug = String(slug ?? '').trim();
  if (!cleanSlug) {
    throw new CardapioError('unavailable', 'Endereço do cardápio incompleto.');
  }

  // Rota pública ANÔNIMA: sem token (o cardápio digital é sempre público/anônimo).
  const headers = new Headers({ Accept: 'application/json' });

  // Canal de EXIBIÇÃO (Fase 6.1a): a mesa-QR pede ?canal=MESA para VER o preço que a mesa COBRA (canal
  // MESA). Sem canal = CARDAPIO (padrão do cardápio online). Assim exibição == cobrança em cada canal.
  const canalQuery = canal === 'MESA' ? '?canal=MESA' : '';

  let res: Response;
  try {
    res = await fetch(apiBase() + '/api/v1/public/cardapio/' + encodeURIComponent(cleanSlug) + canalQuery, {
      method: 'GET',
      headers,
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new CardapioError('network', 'Não foi possível conectar. Verifique sua internet.');
  }

  if (res.status === 401 || res.status === 403 || res.status === 404) {
    throw new CardapioError('unavailable', 'Este cardápio não está disponível.', res.status);
  }
  if (!res.ok) {
    throw new CardapioError('network', 'O cardápio está indisponível no momento.', res.status);
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    throw new CardapioError('network', 'Resposta inesperada do servidor.');
  }
  return normalize(body);
}

/**
 * Slots de agendamento disponíveis de uma data (Fase 2.1). `dataISO` (yyyy-MM-dd) opcional = hoje na zona da
 * loja. Rota pública anônima.
 *
 * QA-28 M-08 — FALHA ≠ LISTA VAZIA. Antes, `!res.ok` e qualquer exceção devolviam `[]` e o checkout
 * imprimia "Sem horários neste dia" — afirmando que a loja não tem agenda quando o que houve foi rede/5xx.
 * O cliente desistia de agendar por causa de um erro que ninguém mostrou. Agora a falha LANÇA CardapioError
 * (o checkout oferece "Tentar de novo") e só uma resposta OK com lista vazia significa "sem horários".
 * Abort continua propagando como antes (o efeito ignora).
 */
export async function fetchSlots(slug: string, dataISO?: string, signal?: AbortSignal): Promise<Slot[]> {
  const cleanSlug = String(slug ?? '').trim();
  if (!cleanSlug) return [];
  const qs = dataISO ? '?data=' + encodeURIComponent(dataISO) : '';
  let res: Response;
  try {
    res = await fetch(apiBase() + '/api/v1/public/cardapio/' + encodeURIComponent(cleanSlug) + '/slots' + qs, {
      method: 'GET',
      headers: new Headers({ Accept: 'application/json' }),
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new CardapioError('network', 'Não conseguimos carregar os horários.');
  }
  if (!res.ok) {
    throw new CardapioError('network', 'Não conseguimos carregar os horários.', res.status);
  }
  try {
    const body = (await res.json()) as unknown;
    return Array.isArray(body) ? (body as Slot[]) : [];
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new CardapioError('network', 'Não conseguimos carregar os horários.', res.status);
  }
}

// ─── POST pedido ─────────────────────────────────────────────────────────────
/**
 * Envia o pedido ao backend. O PREÇO NUNCA vai no corpo — o backend reprecifica.
 * Lança CardapioError em falha (a tela mostra erro e MANTÉM a sacola).
 */
export async function placePedido(slug: string, payload: PedidoPayload): Promise<PedidoConfirmado> {
  const cleanSlug = String(slug ?? '').trim();
  if (!cleanSlug) throw new CardapioError('unavailable', 'Endereço do cardápio incompleto.');

  let res: Response;
  try {
    res = await fetch(
      apiBase() + '/api/v1/public/cardapio/' + encodeURIComponent(cleanSlug) + '/pedidos',
      {
        method: 'POST',
        headers: new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' }),
        body: JSON.stringify(payload),
      }
    );
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new CardapioError('network', 'Não foi possível enviar o pedido.');
  }

  if (!res.ok) {
    // QA lote11 #3 — LÊ o detail/message do corpo (como mesaRequest) para surgir a CAUSA REAL do 4xx
    // (ex.: endereço obrigatório na ENTREGA — ENDERECO_OBRIGATORIO_ENTREGA) em vez de uma mensagem
    // genérica que prendia o cliente num loop de "tente novamente". Sem corpo JSON → mensagem segura.
    let message = 'Não foi possível enviar o pedido.';
    let code: string | undefined;
    let errors: string[] | undefined;
    try {
      const errBody = (await res.json()) as {
        detail?: string;
        message?: string;
        code?: string;
        errors?: unknown;
      };
      message = errBody.detail || errBody.message || message;
      code = errBody.code; // QA bloco13 #4 — carrega o code (ex.: SLOT_INDISPONIVEL) para o storefront tratar.
      // QA-28 M-14 — o 422 de validação do backend manda os detalhes em `errors[]` (o `detail` é fixo e
      // genérico). Carrega a lista para o checkout apontar o CAMPO e reconduzir à etapa certa.
      if (Array.isArray(errBody.errors)) {
        const lista = errBody.errors.filter((e): e is string => typeof e === 'string' && e.trim().length > 0);
        if (lista.length > 0) errors = lista;
      }
    } catch {
      // resposta sem JSON: mantém a mensagem segura
    }
    throw new CardapioError('network', message, res.status, code, errors);
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    throw new CardapioError('network', 'Resposta inesperada do servidor.');
  }
  const b = (body ?? {}) as Record<string, unknown>;
  const numero = toNum(b.numero);
  const status = toStr(b.status);
  // QA ALTO — SUCESSO FALSO: toNum(b.numero) tem default 0, então uma resposta 2xx sem `numero` (ou com
  // 0) virava "Seu pedido #0" na tela de confirmação — sucesso fingido que engana o cliente. Um pedido
  // de verdade SEMPRE tem numero>0 e status preenchido; sem isso, trata como falha (a UI mantém a
  // sacola e permite tentar de novo) em vez de renderizar uma confirmação inválida.
  if (numero <= 0 || !status) {
    throw new CardapioError('network', 'Não foi possível confirmar seu pedido, tente novamente.');
  }
  return { numero, status, total: toNumOrNull(b.total), taxaEntrega: toNumOrNull(b.taxaEntrega) };
}

// Reexport utilitário de sanitização de URL (usado pelo builder de WhatsApp).
export { safeExternalUrl };
